/**
 * Tools Page
 *
 * Admin tools for cache rebuilding, maintenance, and system management.
 * Premium card-based layout with animations and friendly copy.
 *
 * @package PresszoneComments
 */

import API from '../utils/api.js';
import { el, clear, __ } from '../utils/dom.js';
import Button from '../components/Button.js';
import { Card as renderSettingsCard } from '../components/Card.js';
import Confirm from '../utils/confirm.js';

// State
let running = {};
let runningMessages = {};
let successTools = {};

// Rotating messages for running state
const RUNNING_MESSAGES = [
    __('Working...', 'comments-press-zone'),
    __('Almost there...', 'comments-press-zone'),
    __('Just a moment...', 'comments-press-zone'),
    __('Crunching numbers...', 'comments-press-zone'),
    __('Hang tight...', 'comments-press-zone')
];

// Tool definitions with friendly copy
const TOOLS = {
    system: {
        icon: '🔗',
        title: __('System Tools', 'comments-press-zone'),
        description: __('Essential system utilities for when things need a nudge.', 'comments-press-zone'),
        tools: [
            {
                action: 'flush-permalinks',
                icon: '🔗',
                title: __('Flush Permalinks', 'comments-press-zone'),
                description: __('Untangle those URLs!', 'comments-press-zone'),
                help: __('Regenerates WordPress rewrite rules. Useful after changing comment settings.', 'comments-press-zone')
            },
            {
                action: 'repair-database',
                icon: '🗄️',
                title: __('Repair Database Tables', 'comments-press-zone'),
                description: __('Fix missing tables or columns that may cause errors.', 'comments-press-zone'),
                help: __('Re-runs database setup and migrations. Safe to run multiple times - won\'t delete existing data.', 'comments-press-zone')
            },
            {
                action: 'repair-settings',
                icon: '⚙️',
                title: __('Repair Settings', 'comments-press-zone'),
                description: __('Fix missing settings fields (like banned_words).', 'comments-press-zone'),
                help: __('Adds any missing default settings fields without overwriting existing values. Use this if new features aren\'t showing up.', 'comments-press-zone')
            }
        ]
    }
};

/**
 * Render the tools page.
 */
export function renderTools(container, addNotice) {
    clear(container);

    const toolsContainer = el('div', { class: 'presszone-comments-tools presszone-comments-tools--premium' },
        // Header
        el('header', { class: 'presszone-comments-settings__header' },
            el('h1', {}, __('Tools', 'comments-press-zone'))
        ),

        // Tool Cards Grid
        el('div', { class: 'presszone-comments-tools__grid' },
            // System Tools Card
            renderToolsCard('system', container, addNotice, 0)
        )
    );

    container.appendChild(toolsContainer);
}

/**
 * Render a category card with its tools.
 */
function renderToolsCard(categoryKey, container, addNotice, staggerIndex) {
    const category = TOOLS[categoryKey];

    const toolItems = category.tools.map((tool, index) =>
        renderToolItem(tool, container, addNotice, index)
    );

    return renderSettingsCard(
        category.icon,
        category.title,
        category.description,
        toolItems,
        {
            warning: category.warning || false,
            staggerIndex
        }
    );
}

/**
 * Render a single tool item within a card.
 */
function renderToolItem(tool, container, addNotice, index) {
    const isRunning = running[tool.action];
    const isSuccess = successTools[tool.action];

    // Build button content based on state
    let buttonContent;
    let buttonVariant = tool.danger ? 'danger' : 'secondary';

    if (isSuccess) {
        buttonContent = el('span', { class: 'presszone-comments-tool-btn-content' },
            el('span', { class: 'presszone-comments-tool-checkmark' }, '✓'),
            ' ',
            __('Done!', 'comments-press-zone')
        );
        buttonVariant = 'success';
    } else if (isRunning) {
        buttonContent = el('span', { class: 'presszone-comments-tool-btn-content' },
            el('span', { class: 'presszone-comments-tool-spinner' }),
            ' ',
            runningMessages[tool.action] || RUNNING_MESSAGES[0]
        );
    } else {
        buttonContent = __('Run', 'comments-press-zone');
    }

    const toolItem = el('div', {
        class: `presszone-comments-tool-item ${isRunning ? 'presszone-comments-tool-item--running' : ''} ${isSuccess ? 'presszone-comments-tool-item--success' : ''}`,
        style: { '--tool-index': index }
    },
        el('div', { class: 'presszone-comments-tool-item__icon' }, tool.icon),
        el('div', { class: 'presszone-comments-tool-item__content' },
            el('h4', { class: 'presszone-comments-tool-item__title' }, tool.title),
            el('p', { class: 'presszone-comments-tool-item__desc' }, tool.description),
            tool.help ? el('small', { class: 'presszone-comments-tool-item__help' }, tool.help) : null
        ),
        el('div', { class: 'presszone-comments-tool-item__action' },
            Button({
                label: buttonContent,
                variant: buttonVariant,
                disabled: isRunning,
                onClick: () => runTool(tool.action, container, addNotice, tool.danger)
            })
        )
    );

    return toolItem;
}

/**
 * Run a tool action with animated states.
 */
async function runTool(action, container, addNotice, isDanger = false) {
    // Confirm for dangerous actions
    if (isDanger) {
        const confirmed = await Confirm(__('Are you sure? This action cannot be undone.', 'comments-press-zone'), { okVariant: 'danger', icon: '⚠️' });
        if (!confirmed) return;
    }

    running[action] = true;
    runningMessages[action] = RUNNING_MESSAGES[0];
    successTools[action] = false;
    renderTools(container, addNotice);

    // Start rotating messages
    let messageIndex = 0;
    const messageInterval = setInterval(() => {
        messageIndex = (messageIndex + 1) % RUNNING_MESSAGES.length;
        runningMessages[action] = RUNNING_MESSAGES[messageIndex];
        renderTools(container, addNotice);
    }, 2000);

    try {
        await API.post(`/tools/${action}`);

        // Clear running state
        clearInterval(messageInterval);
        running[action] = false;

        // Show success state briefly
        successTools[action] = true;
        renderTools(container, addNotice);

        // Show success notice
        addNotice?.(__('Tool completed successfully! Your comments section is looking great.', 'comments-press-zone'), 'success');

        // Clear success state after animation
        setTimeout(() => {
            successTools[action] = false;
            renderTools(container, addNotice);
        }, 2500);

    } catch (error) {
        clearInterval(messageInterval);
        running[action] = false;
        successTools[action] = false;
        renderTools(container, addNotice);
        addNotice?.(error.message || __('Something went wrong. Please try again.', 'comments-press-zone'), 'error');
    }
}

export default { renderTools };
